import * as THREE from 'three';
import type { Engine } from '../core/Engine';
import { CourseRenderer } from '../rendering/CourseRenderer';
import type { Terrain } from '../terrain/Terrain';
import type { LightingConfig } from '../lighting/types';
import type { EnvironmentConfig } from '../environment/types';
import { DEFAULT_COLOR_GRADE, type ColorGradeConfig } from '../core/colorGrade';
import { Hole } from './Hole';
import { Biome } from './Biome';
import { VegetationField } from './Vegetation';
import { WaterFeature } from './WaterFeature';
import type { CourseMetadata } from './types';
import type { TerrainHeightMapDocument } from './TerrainSculpting';
import type { ObstacleColliderDef } from '../simulation/ObstacleCollision';
import {
  DEFAULT_GREEN_SPEED_PROFILE,
  type GreenSpeedProfile,
} from '../simulation/GreenSpeedProfile';
import { CourseSurfaceClassifier } from './CourseSurfaceClassifier';

export interface CourseOptions {
  metadata: CourseMetadata;
  holes: Hole[];
  /** Hand-placed vegetation instances (exceptions/landmarks) — most background vegetation should come from `biomes` instead. */
  vegetation: VegetationField;
  biomes: Biome[];
  waterFeatures: WaterFeature[];
  lighting: LightingConfig;
  environment: EnvironmentConfig;
  colorGrade?: ColorGradeConfig;
  authoredTerrain?: TerrainHeightMapDocument;
  obstacleColliders?: ObstacleColliderDef[];
  greenSpeed?: GreenSpeedProfile;
}

/**
 * Course owns everything that exists on a golf course: metadata, holes,
 * vegetation, water, and the lighting/environment configuration for this
 * course. It is the single object main.ts hands to Engine to bring a
 * course into the world.
 *
 * Course depends on Engine's public services (scene, assets, lighting,
 * environment) — Engine never imports Course. Terrain is no longer
 * constructed by whoever builds a Course (see Milestone 2); it's built by
 * CourseRenderer during load(), sized/positioned from this course's own
 * hole data (see rendering/computeCourseBounds.ts) — accessing `terrain`
 * before load() throws, since it genuinely doesn't exist yet.
 */
export class Course {
  readonly metadata: CourseMetadata;
  readonly holes: Hole[];
  readonly vegetation: VegetationField;
  readonly biomes: Biome[];
  readonly waterFeatures: WaterFeature[];
  readonly lighting: LightingConfig;
  readonly environment: EnvironmentConfig;
  readonly colorGrade: ColorGradeConfig;
  readonly authoredTerrain?: TerrainHeightMapDocument;
  readonly obstacleColliders: readonly ObstacleColliderDef[];
  readonly greenSpeed: GreenSpeedProfile;
  readonly surfaceClassifier: CourseSurfaceClassifier;

  private readonly root = new THREE.Group();
  private readonly courseRenderer = new CourseRenderer();
  private _terrain: Terrain | null = null;
  private unregisterUpdate: (() => void) | null = null;
  private loaded = false;

  constructor(options: CourseOptions) {
    this.metadata = options.metadata;
    this.holes = options.holes;
    this.vegetation = options.vegetation;
    this.biomes = options.biomes;
    this.waterFeatures = options.waterFeatures;
    this.lighting = options.lighting;
    this.environment = options.environment;
    this.colorGrade = options.colorGrade ?? DEFAULT_COLOR_GRADE;
    this.authoredTerrain = options.authoredTerrain;
    this.obstacleColliders = options.obstacleColliders ?? [];
    this.greenSpeed = options.greenSpeed ?? DEFAULT_GREEN_SPEED_PROFILE;
    this.surfaceClassifier = new CourseSurfaceClassifier(this.holes);
    this.root.name = `Course:${this.metadata.id}`;
  }

  /** Throws before load() — terrain is built by CourseRenderer during load, not at construction. */
  get terrain(): Terrain {
    if (!this._terrain) {
      throw new Error(`Course "${this.metadata.id}": terrain accessed before load() completed`);
    }
    return this._terrain;
  }

  getHole(number: number): Hole | undefined {
    return this.holes.find((hole) => hole.number === number);
  }

  get isLoaded(): boolean {
    return this.loaded;
  }

  /** Wires this course's data into Engine's generic services and builds the visual scene via CourseRenderer. */
  async load(engine: Engine): Promise<void> {
    if (this.loaded) return;

    engine.scene.add(this.root);
    this._terrain = await this.courseRenderer.render(this, engine.assets, this.root);

    engine.lighting.applyConfig(this.lighting);
    await engine.environment.applyConfig(this.environment);
    engine.applyColorGrade(this.colorGrade);

    this.unregisterUpdate = engine.onUpdate((deltaSeconds) =>
      this.courseRenderer.update(deltaSeconds),
    );

    this.loaded = true;
  }

  /** Reverses load(): removes everything this course added and releases its resources. */
  unload(): void {
    if (!this.loaded) return;

    this.unregisterUpdate?.();
    this.unregisterUpdate = null;
    this.courseRenderer.dispose();
    this._terrain?.dispose();
    this._terrain = null;
    this.root.parent?.remove(this.root);

    this.loaded = false;
  }
}
